/**
 * Welche Einstellung gilt für das Konto, welche nur für dieses Gerät?
 * (23.09.2026, Ticket 2511)
 *
 * Martin: *„Einstellungen sollen zentral und nicht je Gerät gelten. Wer etwas
 * einmal einstellt, erwartet es auf jedem Gerät, genau wie beim Profil."* —
 * und: *„Werte, die wirklich zum Gerät gehören (Mikrofon, Lautsprecher,
 * Bluetooth-Verhalten), bleiben ausdrücklich gerätebezogen und stehen als
 * solche gekennzeichnet in einer eigenen Liste. Alles andere gilt für das
 * Konto."*
 *
 * ## Die Regel
 *
 * Ein Feld ist ein KONTOWERT, solange es hier nicht steht. Der Server ist
 * führend, die jüngste Änderung gewinnt (server/einstellungen-zeit.ts).
 *
 * Ein GERÄTEWERT hängt an Mikrofon, Lautsprecher oder Sensor DIESES Geräts.
 * Er wird nie vom Server über einen eigenen Wert gelegt. Liegt er in den
 * Einstellungen (`speicher: "voice-v8.settings"`), geht er trotzdem an den
 * Server — aber nur als VORLAGE für ein neues Gerät, damit eine
 * Neuinstallation ihn nicht verliert (Ticket 745). Ein Gerät, das schon einen
 * eigenen Wert hat, übernimmt die Vorlage nie.
 *
 * ⚠️ Warum wachwort und alwaysRecord hier stehen: Beide lauschen über das
 *    Mikrofon DIESES Geräts, nur in der iPhone-App, mit einer Erlaubnis je
 *    Gerät. Als Kontowert würde ein Gerät ohne Erlaubnis (settings-view setzt
 *    den Schalter dann auf „aus" zurück) das Wach-Wort auf allen Geräten
 *    abschalten, und zwei Telefone würden den Tag doppelt aufnehmen.
 *
 * ⚠️ Darstellung (Helligkeit, Palette, Zuhause-Stil) ist KONTO, gehört aber
 *    appearance.ts. Der Abgleich schreibt deren Schlüssel nie selbst, sondern
 *    reicht die Serverwerte an appearance.ts weiter (ein Besitzer je Zustand).
 */

/** Der Schlüssel des Einstellungs-Zwischenspeichers im Browser. */
export const EINSTELLUNGEN_SCHLUESSEL = "voice-v8.settings";

/**
 * Älter als 30 Tage zählt eine Änderung nicht — sie gilt dann als so alt.
 * Gerät und Server rechnen mit derselben Grenze.
 */
export const ALTER_MAX_MS = 30 * 24 * 60 * 60 * 1000;

interface GeraeteWert {
  /** Name des Felds bzw. der Einstellung. */
  feld: string;
  /** Wo der Wert auf dem Gerät liegt. */
  speicher: string;
  /** Warum er zum Gerät gehört und nicht zum Konto. */
  grund: string;
}

/** DIE Liste der Gerätewerte. Alles, was hier fehlt, gilt für das Konto. */
export const GERAETE_WERTE: readonly GeraeteWert[] = [
  {
    feld: "wachwort",
    speicher: EINSTELLUNGEN_SCHLUESSEL,
    grund:
      "„Hey Vespera“ lauscht dauerhaft über das Mikrofon dieses Geräts; die iOS-Erlaubnis gilt je Gerät.",
  },
  {
    feld: "alwaysRecord",
    speicher: EINSTELLUNGEN_SCHLUESSEL,
    grund:
      "Die Tagesaufnahme läuft über das Mikrofon dieses Geräts; zwei Geräte nähmen den Tag doppelt auf.",
  },
  {
    feld: "sttSensitivity",
    speicher: EINSTELLUNGEN_SCHLUESSEL,
    grund: "Die Empfindlichkeit hängt an Mikrofon und Umgebung dieses Geräts.",
  },
  {
    feld: "lautsprecher",
    speicher: "voice-v8.autoplay",
    grund: "Lautsprecher an oder stumm — das Telefon in der Tasche ist nicht der Rechner am Schreibtisch.",
  },
  {
    feld: "autoStandort",
    speicher: "voice-v8.auto-location",
    grund: "GPS und Standort-Erlaubnis gehören dem Gerät; der Rechner soll nicht den Ort des Telefons melden.",
  },
  {
    feld: "pttNativ",
    speicher: "voice-v8.pttNativ",
    grund: "Aufnahmeweg der Sprechtaste auf diesem Gerät (Entwicklerschalter).",
  },
  {
    feld: "verbindungsToene",
    speicher: "voice-v8.connection-tones",
    grund: "Klang beim Verbinden auf diesem Gerät.",
  },
];

/** Gerätewerte, die im Einstellungs-Zwischenspeicher liegen. */
export const GERAETE_FELDER: ReadonlySet<string> = new Set(
  GERAETE_WERTE.filter((w) => w.speicher === EINSTELLUNGEN_SCHLUESSEL).map(
    (w) => w.feld,
  ),
);

/**
 * Kontowerte mit eigenem Besitzer: appearance.ts schreibt ihre Schlüssel
 * (voice-v8.theme, voice-v8.palette, voice-v8.zuhause-stil), der Abgleich
 * reicht nur weiter.
 */
export const DARSTELLUNG_FELDER: ReadonlySet<string> = new Set([
  "themeMode",
  "themePalette",
  "zuhauseStil",
]);

/** Felder, die niemand mehr liest — sie werden nicht abgeglichen. */
export const ABGELEGTE_FELDER: ReadonlySet<string> = new Set(["chatStyle"]);

export function istGeraeteFeld(feld: string): boolean {
  return GERAETE_FELDER.has(feld);
}

/** Nur die Gerätewerte aus einem Einstellungs-Objekt (z. B. für /api/token). */
export function nurGeraeteFelder(roh: unknown): Record<string, unknown> {
  if (!roh || typeof roh !== "object" || Array.isArray(roh)) return {};
  const aus: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(roh as Record<string, unknown>)) {
    if (GERAETE_FELDER.has(k)) aus[k] = v;
  }
  return aus;
}
